import { useCallback, useMemo } from 'react';
import useWebSocketOriginal, { ReadyState } from 'react-use-websocket';

const readyStateToString = (readyState) =>
  ({
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  })[readyState];

// The collab websocket host is injected at build time and is managed via
// Terraform/tfvars (`REACT_APP_COLLAB_WS_HOST`), one value per environment.
// These must agree with the environments defined in `core/collab/wrangler.toml`.
// An empty value signals that realtime collab is disabled for this
// environment/instance: callers (e.g. 1:1 agendas) continue to work via async
// REST persistence, we simply never open a realtime editing connection.
export const isCollabEnabled = (): boolean =>
  Boolean(process.env.REACT_APP_COLLAB_WS_HOST);

// this is a re-usable hook for creating a websocket that connects to our
// Cloudflare Durable Objects. this should not require any modification to
// support other kinds of Durable Objects beyond 1:1 agendas, since protocol
// details are left to the caller.
export const useWebSocket = (token, onMessage) => {
  const hostname = process.env.REACT_APP_COLLAB_WS_HOST || '';

  // Only open a connection when collab is enabled (a host is configured) and we
  // have a token. When the host is empty, collab is disabled. sometimes token
  // will be undefined when this hook is used before the component is fully set
  // up. passing 'null' to react-use-websocket results in a NOP, so we'll do
  // that in both cases.
  const url = hostname && token ? `${hostname}/?token=${token}` : null;

  // note: because this is a hook we cannot short-circuit logic to
  // avoid calling this if we know the url is invalid
  const { sendMessage, readyState } = useWebSocketOriginal(url, {
    onMessage,
    shouldReconnect: () => true,
  });

  const send = useCallback(
    (payload) => {
      sendMessage(JSON.stringify(payload));
    },
    [sendMessage]
  );

  const status = useMemo(() => readyStateToString(readyState), [readyState]);

  // if collab is disabled (no host) or the token is undefined, we explicitly
  // return something that is safe but does nothing, so callers degrade to
  // async-only editing.
  if (!hostname || !token) {
    return [
      () => {
        /* NOP */
      },
      'Uninstantiated',
    ];
  }

  return [send, status];
};
